import { Helmet } from "react-helmet-async";
import { getPageSeo, getPageTitle } from "@/lib/pageSeo";

const BASE_URL = "https://eridonai.ru";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-default.jpg`;

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  /** Absolute or root-relative path to OG image (1200×630 recommended). */
  ogImage?: string;
  /** JSON-LD structured data objects to inject */
  jsonLd?: Record<string, unknown>[];
  noIndex?: boolean;
}

export function SEOHead({ title, description, path, ogImage, jsonLd, noIndex }: SEOHeadProps) {
  const { canonicalPath, metadata } = getPageSeo(path);
  const canonical = `${BASE_URL}${canonicalPath}`;
  const pageTitle = metadata?.title ?? title;
  const pageDescription = metadata?.description ?? description;
  const fullTitle = getPageTitle(canonicalPath, pageTitle);
  const shouldNoIndex = noIndex || metadata?.noIndex;
  const image = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : `${BASE_URL}${ogImage.startsWith("/") ? "" : "/"}${ogImage}`
    : DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <html lang="ru" />
      <title>{fullTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:locale" content="ru_RU" />
      <meta property="og:site_name" content="ERIDON AI" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={image} />

      <meta name="robots" content={shouldNoIndex ? "noindex, follow" : "index, follow, max-image-preview:large"} />

      {jsonLd?.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}
